/**
 * Edge routing for the workflow builder canvas.
 *
 * The layout stacks nodes in rows, so an edge between adjacent rows is a short
 * vertical hop with nothing in the way. An edge that *skips* a row is the
 * problem: a straight line from an agent on row 1 to the synthesizer on row 3
 * runs clean through whatever sits on row 2, and reads as though it connects to
 * that node rather than passing it. Conditional edges make it worse, because a
 * dashed line crossing a node looks like it terminates there.
 *
 * So: keep the plain vertical cubic whenever the straight route is clear, and
 * bow the edge sideways around the obstacles when it is not. Pure geometry over
 * rectangles in unzoomed canvas coordinates, like ./workflowCanvasLayout — no
 * DOM measurement, deterministic, and testable on its own.
 */

import { inflate } from "./workflowCanvasLayout";

/** Clearance kept between a routed edge and the node it steers around. */
export const EDGE_CLEARANCE = 16;

/** How far out the first detour attempt bows; later attempts step further. */
export const DETOUR_STEP = 52;

/**
 * Detour attempts per side before giving up. An edge spanning several full rows
 * has to clear the whole stack, so the search must be able to reach past the
 * widest row — hence a generous number of steps rather than a handful.
 */
export const DETOUR_TRIES = 14;

/**
 * Which faces an edge leaves and enters.
 *
 * A layered flow diagram reads top-to-bottom, so an edge **always enters its
 * target at the top-centre** — that is what makes the direction of flow legible
 * at a glance, and it is what the consumer viewer draws too. An earlier attempt
 * joined side-by-side nodes at their facing left/right edges; that made a
 * same-row edge shorter but broke the convention, so a graph mixing the two
 * read as though its arrows meant different things.
 *
 * The source face is the one bit of freedom: an edge leaves the bottom when its
 * target is below (the normal case) and the top when the target is above, so it
 * never has to double back through its own node.
 *
 * `from` and `to` are `{x, y, w, h}`.
 */
export const edgeEndpoints = (from, to) => {
  const targetIsAbove = (to.y + to.h) <= from.y;
  return {
    x1: from.x + from.w / 2,
    y1: targetIsAbove ? from.y : from.y + from.h,
    x2: to.x + to.w / 2,
    y2: targetIsAbove ? to.y + to.h : to.y,
  };
};

/**
 * Straight vertical cubic: leaves the source's bottom edge, enters the target's
 * top edge. Control points are purely vertical, so it never detours sideways.
 */
export const straightPath = (x1, y1, x2, y2) => {
  const c = Math.max(28, Math.abs(y2 - y1) * 0.4);
  return `M ${x1} ${y1} C ${x1} ${y1 + c}, ${x2} ${y2 - c}, ${x2} ${y2}`;
};

/**
 * Control points for a route. `bowX === null` is the straight vertical cubic;
 * otherwise both control points sit out at `bowX`, holding the curve on that
 * side for the whole span rather than only bulging at the waist. Holding the
 * line out is what lets an edge clear a *fully populated* row it has to cross —
 * a midpoint bulge just swings back into the row it was trying to avoid.
 */
const controls = (x1, y1, x2, y2, bowX) => {
  const c = Math.max(30, Math.abs(y2 - y1) * 0.45);
  if (bowX === null) return { cx1: x1, cy1: y1 + c, cx2: x2, cy2: y2 - c };
  // Pull the control points close to the endpoints vertically as well as out to
  // `bowX`. The curve then leaves its node heading sideways rather than straight
  // down, which is the only way past an obstacle sitting directly beneath the
  // source and directly above the target — the common "skips a row in the same
  // column" case, where any near-vertical departure clips the node between.
  return { cx1: bowX, cy1: y1 + c * 0.25, cx2: bowX, cy2: y2 - c * 0.25 };
};

/**
 * A cubic held out at `bowX` — the shape used to steer an edge around nodes.
 * It leaves and re-enters close to vertical, so the endpoints still read as
 * "out of the bottom" and "into the top" of their nodes.
 */
export const bowedPath = (x1, y1, x2, y2, bowX) => {
  const { cx1, cy1, cx2, cy2 } = controls(x1, y1, x2, y2, bowX);
  return `M ${x1} ${y1} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${x2} ${y2}`;
};

/**
 * Points sampled along the route, used to test it against node rectangles.
 * `samples` is generous enough that no node box can slip between two samples.
 */
export const sampleCubic = (x1, y1, x2, y2, bowX, samples = 40) => {
  const { cx1, cy1, cx2, cy2 } = controls(x1, y1, x2, y2, bowX);
  const pts = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples, u = 1 - t;
    pts.push({
      x: u * u * u * x1 + 3 * u * u * t * cx1 + 3 * u * t * t * cx2 + t * t * t * x2,
      y: u * u * u * y1 + 3 * u * u * t * cy1 + 3 * u * t * t * cy2 + t * t * t * y2,
    });
  }
  return pts;
};

/** Does any sampled point of this route fall inside an obstacle? */
export const routeHits = (x1, y1, x2, y2, bowX, obstacles) => {
  const pts = sampleCubic(x1, y1, x2, y2, bowX);
  return obstacles.some(o => pts.some(p => (
    p.x >= o.x && p.x <= o.x + o.w && p.y >= o.y && p.y <= o.y + o.h
  )));
};

/**
 * Obstacles an edge must avoid: every node box except the two it connects.
 * Endpoints are excluded because the edge is *supposed* to touch them.
 *
 * `boxes` is a map of uid → `{x, y, w, h}`.
 */
export const obstaclesFor = (boxes, fromUid, toUid, clearance = EDGE_CLEARANCE) =>
  Object.entries(boxes)
    .filter(([uid]) => uid !== fromUid && uid !== toUid)
    .map(([, box]) => inflate(box, clearance));

/**
 * An S-shaped route that steps out to `laneX` for the middle of its span and
 * returns. Unlike a bow, this genuinely leaves the source's column, so it can
 * pass an obstacle sitting directly between two nodes that share a column —
 * the case no single cubic can solve.
 */
export const lanePath = (x1, y1, x2, y2, laneX) => {
  const dy = y2 - y1;
  const a = y1 + dy * 0.3, b = y2 - dy * 0.3;
  return `M ${x1} ${y1}`
    + ` C ${x1} ${y1 + dy * 0.15}, ${laneX} ${a - dy * 0.1}, ${laneX} ${a}`
    + ` L ${laneX} ${b}`
    + ` C ${laneX} ${b + dy * 0.1}, ${x2} ${y2 - dy * 0.15}, ${x2} ${y2}`;
};

/** Sampled points for the lane route above. */
export const sampleLane = (x1, y1, x2, y2, laneX, samples = 40) => {
  const dy = y2 - y1;
  const a = y1 + dy * 0.3, b = y2 - dy * 0.3;
  const cubic = (p0, p1, p2, p3, t) => {
    const u = 1 - t;
    return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
  };
  const pts = [];
  const third = Math.max(2, Math.round(samples / 3));
  for (let i = 0; i <= third; i++) {
    const t = i / third;
    pts.push({
      x: cubic(x1, x1, laneX, laneX, t),
      y: cubic(y1, y1 + dy * 0.15, a - dy * 0.1, a, t),
    });
  }
  for (let i = 1; i < third; i++) pts.push({ x: laneX, y: a + (b - a) * (i / third) });
  for (let i = 0; i <= third; i++) {
    const t = i / third;
    pts.push({
      x: cubic(laneX, laneX, x2, x2, t),
      y: cubic(b, b + dy * 0.1, y2 - dy * 0.15, y2, t),
    });
  }
  return pts;
};

/** Does the lane route touch an obstacle? */
export const laneHits = (x1, y1, x2, y2, laneX, obstacles) => {
  const pts = sampleLane(x1, y1, x2, y2, laneX);
  return obstacles.some(o => pts.some(p => (
    p.x >= o.x && p.x <= o.x + o.w && p.y >= o.y && p.y <= o.y + o.h
  )));
};

/**
 * The path for one edge: straight when the direct route is clear, otherwise
 * bowed around whatever is in the way, and finally stepped out into a clear
 * lane when even a bow cannot pass.
 *
 * Detours are tried alternating right then left, stepping further out each
 * round, and the first clear route wins. Trying both sides symmetrically keeps
 * an edge on the near side of the graph rather than always sweeping right, and
 * the fixed try order makes the result deterministic — the same graph always
 * draws the same way, which matters because these are re-rendered constantly.
 *
 * Falls back to the straight path when nothing clears, so a dense graph still
 * draws every edge rather than dropping one.
 */
export function routeEdge(x1, y1, x2, y2, obstacles = []) {
  if (!obstacles.length || !routeHits(x1, y1, x2, y2, null, obstacles)) {
    return { d: straightPath(x1, y1, x2, y2), detoured: false, bowX: null };
  }

  // Step out from whichever end is furthest that way, so a detour to the right
  // starts right of both endpoints instead of somewhere between them.
  const right = Math.max(x1, x2), left = Math.min(x1, x2);
  for (let i = 1; i <= DETOUR_TRIES; i++) {
    for (const dir of [1, -1]) {
      const bowX = dir > 0 ? right + DETOUR_STEP * i : left - DETOUR_STEP * i;
      if (!routeHits(x1, y1, x2, y2, bowX, obstacles)) {
        return { d: bowedPath(x1, y1, x2, y2, bowX), detoured: true, bowX };
      }
    }
  }
  // Nothing bowed clear — step fully out of the column into an empty lane.
  for (let i = 1; i <= DETOUR_TRIES; i++) {
    for (const dir of [1, -1]) {
      const laneX = dir > 0 ? right + DETOUR_STEP * i : left - DETOUR_STEP * i;
      if (!laneHits(x1, y1, x2, y2, laneX, obstacles)) {
        return { d: lanePath(x1, y1, x2, y2, laneX), detoured: true, laneX, bowX: null };
      }
    }
  }

  return { d: straightPath(x1, y1, x2, y2), detoured: false, bowX: null };
}

export default routeEdge;
